import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import {
  startCopyTradeEngine,
  stopCopyTradeEngine,
  isCopyTradeEngineRunning,
  forceApplyConfigs,
  COPY_TRADE_PAIRS_LS_KEY,
  deactivateDesiredState,
  clearPairSignature,
  resolveCopyTradePairs,
} from '@/lib/copy-trade-engine';
import type { CopyConfig } from '@/lib/copy-trade-engine';
import { saveCopyTradeConfig, markCopyTradeConfigUpdatedLocally } from '@/lib/copy-trade-persistence';

// ─── Types ──────────────────────────────────────────────────

interface CtraderAccount {
  id: number;
  account_id: string;
  account_name: string;
  is_live: boolean;
}

interface Mt4Account {
  account_id: string;
  account_name: string;
  is_live: boolean;
}

interface DesiredPosition {
  symbolName: string;
  tradeSide: string;
  volume: number;
  sl: number | null;
  tp: number | null;
}

interface StateEntry {
  sourceAccountId: string;
  targetMt4AccountId: string;
  isActive: boolean;
  positions: DesiredPosition[];
  updatedAt: string;
}

interface CopyPair {
  sourceAccountId: string;
  sourceAccountName: string;
  targetMt4AccountId: string;
  targetMt4AccountName: string;
  strategyId?: string;
  strategyName?: string;
  enabled?: boolean;
  volumeMode?: 'same' | 'percent' | 'fixed';
  volumePercent?: number;
  label?: string;
}

interface LateApprovalConfig {
  target_mt4_account_id: string;
  enabled: boolean;
  grace_minutes: number;
}

interface ApprovalRow {
  id: number;
  source_account_id: string;
  target_mt4_account_id: string;
  position_id: number;
  symbol_name: string | null;
  trade_side: string | null;
  volume: number | null;
  source_open_time: string | null;
  status: string;
  requested_at: string;
  responded_at: string | null;
}

// ─── Helpers ────────────────────────────────────────────────

function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

const LS_KEY_PAIRS = COPY_TRADE_PAIRS_LS_KEY;

// Persist pairs to localStorage AND stamp the edit time, so restore can tell
// this-browser edits apart from a stale cross-device copy.
function persistPairsLocal(updated: CopyPair[]): void {
  localStorage.setItem(LS_KEY_PAIRS, JSON.stringify(updated));
  markCopyTradeConfigUpdatedLocally();
}

function pairToConfig(p: CopyPair): CopyConfig {
  return {
    sourceAccountId: p.sourceAccountId,
    targetMt4AccountId: p.targetMt4AccountId,
    strategyId: p.strategyId,
    enabled: p.enabled !== false,
    volumeMode: p.volumeMode || 'same',
    volumePercent: p.volumePercent,
    fixedVolume: p.fixedVolume,
  };
}

// Grace-window minutes editor. Keeps a LOCAL draft and commits on blur/Enter,
// so typing does not fire a DB write per keystroke (and the field can be
// cleared while editing without being force-reset to 1).
function LateGraceInput({ value, onCommit }: { value: number; onCommit: (n: number) => void }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  return (
    <input
      type="number"
      min="1"
      step="1"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const n = Math.max(1, Math.floor(Number(draft) || 1));
        setDraft(String(n));
        if (n !== value) onCommit(n);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      className="w-16 px-2 py-1.5 bg-background-100 border border-background-300/60 rounded-md text-[10px] text-foreground-900 focus:outline-none focus:border-primary-500/50"
    />
  );
}

// ─── Component ──────────────────────────────────────────────

interface CopyTradeMonitorProps {
  isCtraderConnected: boolean;
  ctraderAccounts: CtraderAccount[];
  mt4Accounts: Mt4Account[];
}

export default function CopyTradeMonitor({
  isCtraderConnected,
  ctraderAccounts,
  mt4Accounts,
}: CopyTradeMonitorProps) {
  const [engineRunning, setEngineRunning] = useState(false);
  const [pairs, setPairs] = useState<CopyPair[]>([]);
  const [stateEntries, setStateEntries] = useState<StateEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showAddPair, setShowAddPair] = useState(false);
  const [confirmRemoveIndex, setConfirmRemoveIndex] = useState<number | null>(null);
  const [editingLabelIndex, setEditingLabelIndex] = useState<number | null>(null);
  const [labelInput, setLabelInput] = useState('');

  const [newSourceId, setNewSourceId] = useState('');
  const [newTargetId, setNewTargetId] = useState('');

  const [approvals, setApprovals] = useState<ApprovalRow[]>([]);
  const [lateConfigs, setLateConfigs] = useState<Record<string, LateApprovalConfig>>({});
  const [approvalBusyId, setApprovalBusyId] = useState<number | null>(null);
  const [approvalView, setApprovalView] = useState<'pending' | 'history'>('pending');
  const [historyFilter, setHistoryFilter] = useState<'all' | 'approved' | 'rejected'>('all');
  const [approveAllBusy, setApproveAllBusy] = useState(false);
  const [rejectAllBusy, setRejectAllBusy] = useState(false);

  const logPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const applyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [appliedIndex, setAppliedIndex] = useState<number | null>(null);

  // Load saved pairs — freshest of {Supabase (cross-device), localStorage (this
  // browser)}. If the engine is already running, force it onto this config so a
  // running engine can never keep applying an older volume setting.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const resolved = await resolveCopyTradePairs();
      if (cancelled) return;
      if (resolved.pairs.length === 0) return;
      const loadedPairs = resolved.pairs as CopyPair[];
      setPairs(loadedPairs);
      localStorage.setItem(LS_KEY_PAIRS, JSON.stringify(loadedPairs));
      if (isCopyTradeEngineRunning()) {
        startCopyTradeEngine(loadedPairs.map(pairToConfig));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Save pairs
  const savePairs = (updated: CopyPair[]) => {
    setPairs(updated);
    persistPairsLocal(updated);
    void saveCopyTradeConfig(updated, isCopyTradeEngineRunning());
  };

  // Poll desired-state (open positions being copied)
  const fetchState = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      const { data } = await supabase
        .from('copy_trade_state')
        .select('source_account_id, target_mt4_account_id, position_snapshot, is_active, updated_at')
        .order('updated_at', { ascending: false })
        .limit(20);

      if (data) {
        const entries: StateEntry[] = (data as Array<Record<string, unknown>>).map((row) => ({
          sourceAccountId: row.source_account_id as string,
          targetMt4AccountId: row.target_mt4_account_id as string,
          isActive: row.is_active === true,
          positions: (Array.isArray(row.position_snapshot)
            ? (row.position_snapshot as Array<Record<string, unknown>>).map((p) => ({
                symbolName: (typeof p.symbolName === 'string' ? p.symbolName : '') || '',
                tradeSide: p.tradeSide === 'SELL' ? 'SELL' : 'BUY',
                volume: typeof p.volume === 'number' ? p.volume : 0,
                sl: typeof p.sl === 'number' ? p.sl : null,
                tp: typeof p.tp === 'number' ? p.tp : null,
              }))
            : []),
          updatedAt: (row.updated_at as string) || '',
        }));
        setStateEntries(entries);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (engineRunning) {
      void fetchState();
      logPollRef.current = setInterval(fetchState, 2000);
    }
    return () => {
      if (logPollRef.current) clearInterval(logPollRef.current);
    };
  }, [engineRunning, fetchState]);

  // Late-entry approvals + their per-target config. Polled independently of the
  // engine so the web panel reflects Telegram/manual decisions within seconds.
  const fetchApprovals = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      const { data } = await supabase
        .from('copy_trade_approvals')
        .select('id, source_account_id, target_mt4_account_id, position_id, symbol_name, trade_side, volume, source_open_time, status, requested_at, responded_at')
        .order('requested_at', { ascending: false })
        .limit(50);
      if (data && mountedRef.current) setApprovals(data as ApprovalRow[]);
    } catch { /* ignore */ }
  }, []);

  const fetchLateConfigs = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      const { data } = await supabase
        .from('copy_trade_late_config')
        .select('target_mt4_account_id, enabled, grace_minutes');
      if (data && mountedRef.current) {
        const map: Record<string, LateApprovalConfig> = {};
        for (const row of data as LateApprovalConfig[]) map[row.target_mt4_account_id] = row;
        setLateConfigs(map);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    void fetchApprovals();
    void fetchLateConfigs();
    const t = setInterval(() => { void fetchApprovals(); }, 3000);
    return () => clearInterval(t);
  }, [fetchApprovals, fetchLateConfigs]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (applyTimerRef.current) clearTimeout(applyTimerRef.current);
    };
  }, []);

  // Sync button with the real (module-level) engine state on mount.
  useEffect(() => {
    setEngineRunning(isCopyTradeEngineRunning());
  }, []);

  // Start / Stop engine
  const handleToggleEngine = () => {
    if (engineRunning) {
      stopCopyTradeEngine();
      setEngineRunning(false);
      setError(null);
      void saveCopyTradeConfig(pairs, false);
    } else {
      if (pairs.length === 0) {
        setError('Add at least one copy pair before starting.');
        return;
      }
      const enabledCount = pairs.filter((p) => p.enabled !== false).length;
      if (enabledCount === 0) {
        setError('Enable at least one copy pair before starting.');
        return;
      }
      try {
        const configs: CopyConfig[] = pairs.map(pairToConfig);
        startCopyTradeEngine(configs);
        setEngineRunning(true);
        setError(null);
        void saveCopyTradeConfig(pairs, true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start engine');
      }
    }
  };

  // Add pair
  const handleAddPair = () => {
    if (!newSourceId || !newTargetId) return;

    if (pairs.some((p) => p.sourceAccountId === newSourceId && p.targetMt4AccountId === newTargetId)) {
      setError('This pair already exists.');
      return;
    }

    const source = ctraderAccounts.find((a) => a.account_id === newSourceId);
    const target = mt4Accounts.find((a) => a.account_id === newTargetId);

    const newPair: CopyPair = {
      sourceAccountId: newSourceId,
      sourceAccountName: source?.account_name || newSourceId,
      targetMt4AccountId: newTargetId,
      targetMt4AccountName: target?.account_name || newTargetId,
      enabled: true,
    };

    savePairs([...pairs, newPair]);
    setNewSourceId('');
    setNewTargetId('');
    setShowAddPair(false);
    setError(null);
  };

  // Label edit
  const handleStartEditLabel = (index: number) => {
    setEditingLabelIndex(index);
    setLabelInput(pairs[index].label || '');
  };
  const handleSaveLabel = (index: number) => {
    const updated = pairs.map((p, i) => (i === index ? { ...p, label: labelInput.trim() || undefined } : p));
    savePairs(updated);
    setEditingLabelIndex(null);
    setLabelInput('');
  };
  const handleCancelEditLabel = () => {
    setEditingLabelIndex(null);
    setLabelInput('');
  };

  // Remove pair from DB state (used in the sync state list)
  const handleDeleteStateEntry = async (entry: StateEntry) => {
    // 1. Remove from DB copy_trade_state
    try {
      await supabase
        .from('copy_trade_state')
        .delete()
        .eq('source_account_id', entry.sourceAccountId)
        .eq('target_mt4_account_id', entry.targetMt4AccountId);
    } catch (err) {
      console.warn('[CopyTradeMonitor] delete state entry failed:', err);
    }

    // 2. Also remove from pairs config if it exists there
    const pairIdx = pairs.findIndex(
      (p) => p.sourceAccountId === entry.sourceAccountId && p.targetMt4AccountId === entry.targetMt4AccountId
    );
    if (pairIdx !== -1) {
      await handleRemovePair(pairIdx);
    }

    // 3. Refresh the state list
    void fetchState();
  };

  // Remove pair
  const handleRemovePair = async (index: number) => {
    const pair = pairs[index];
    const updated = pairs.filter((_, i) => i !== index);

    if (engineRunning) {
      // 1) Deactivate this pair in DB so MT4 EA stops copying for it
      await deactivateDesiredState({
        sourceAccountId: pair.sourceAccountId,
        targetMt4AccountId: pair.targetMt4AccountId,
      });
      // 2) Clear its dedup signature so re-adding later works correctly
      clearPairSignature({
        sourceAccountId: pair.sourceAccountId,
        targetMt4AccountId: pair.targetMt4AccountId,
      });
      // 3) If no pairs left, stop the engine entirely
      if (updated.length === 0) {
        stopCopyTradeEngine();
        setEngineRunning(false);
      } else {
        // Update engine configs with remaining pairs
        startCopyTradeEngine(updated.map(pairToConfig));
      }
    }

    savePairs(updated);
    setConfirmRemoveIndex(null);
  };

  // Toggle a single pair ON/OFF
  //
  // OFF must ONLY stop the engine from adjusting that pair — it must NEVER
  // close the positions already mirrored on MT4. We therefore explicitly
  // FREEZE the pair (the MT4 EA returns early on an inactive state and leaves
  // the mirrored orders untouched). We only mark the target inactive when no
  // OTHER active source feeds the same MT4 account; if the target is shared,
  // marking this row inactive would remove its positions from the aggregated
  // snapshot and make the EA close them — so in that case we leave the state
  // alone (the engine simply stops updating it → it freezes naturally).
  const handleTogglePair = async (index: number) => {
    const pair = pairs[index];
    const wasEnabled = pair.enabled !== false;

    const updated = pairs.map((p, i) =>
      i === index ? { ...p, enabled: p.enabled === false } : p
    );
    setPairs(updated);
    persistPairsLocal(updated);
    void saveCopyTradeConfig(updated, isCopyTradeEngineRunning());

    // Reset dedup + safety memory so a later re-enable is never skipped.
    clearPairSignature({
      sourceAccountId: pair.sourceAccountId,
      targetMt4AccountId: pair.targetMt4AccountId,
    });

    if (!isCopyTradeEngineRunning()) return;

    // Live-update engine configs (the engine ignores disabled pairs).
    startCopyTradeEngine(updated.map(pairToConfig));

    // Toggling OFF → freeze this pair's target without closing anything.
    if (wasEnabled) {
      let targetShared = false;
      try {
        const { data: others } = await supabase
          .from('copy_trade_state')
          .select('source_account_id')
          .eq('target_mt4_account_id', pair.targetMt4AccountId)
          .eq('is_active', true)
          .neq('source_account_id', pair.sourceAccountId)
          .limit(1);
        targetShared = Array.isArray(others) && others.length > 0;
      } catch {
        // On any doubt, do NOT touch the state (safest).
        targetShared = true;
      }

      if (!targetShared) {
        void deactivateDesiredState({
          sourceAccountId: pair.sourceAccountId,
          targetMt4AccountId: pair.targetMt4AccountId,
        });
      } else {
        console.warn(
          `[CopyTradeMonitor] Pair OFF — target ${pair.targetMt4AccountId} is shared with another active source; leaving state untouched (frozen, no close).`
        );
      }
    }
  };

  // Apply a pair update (persist + live-sync to engine when running)
  const applyPairUpdate = (updated: CopyPair[]) => {
    setPairs(updated);
    persistPairsLocal(updated);
    void saveCopyTradeConfig(updated, isCopyTradeEngineRunning());

    if (isCopyTradeEngineRunning()) {
      // Update the live engine config AND force an IMMEDIATE rewrite of the
      // desired state, so a volume change takes effect right away instead of
      // waiting for the next positions event. forceApplyConfigs is a no-op when
      // no positions snapshot is cached yet (the next event then applies it).
      startCopyTradeEngine(updated.map(pairToConfig));
      forceApplyConfigs(updated.map(pairToConfig));
    }
  };

  // Volume transform: mode
  const updateVolumeMode = (index: number, mode: 'same' | 'percent' | 'fixed') => {
    applyPairUpdate(pairs.map((p, i) => (i === index ? { ...p, volumeMode: mode } : p)));
  };

  // Volume transform: percent
  const updateVolumePercent = (index: number, raw: string) => {
    const num = parseFloat(raw);
    applyPairUpdate(
      pairs.map((p, i) => (i === index ? { ...p, volumePercent: Number.isNaN(num) ? undefined : num } : p))
    );
  };

  // Volume transform: fixed lot
  const updateFixedVolume = (index: number, raw: string) => {
    const num = parseFloat(raw);
    applyPairUpdate(
      pairs.map((p, i) => (i === index ? { ...p, fixedVolume: Number.isNaN(num) ? undefined : num } : p))
    );
  };

  // Manual "Apply" — force-push the current volume config to the engine right now.
  // This is a fallback in case the auto (on-change) sync didn't take effect.
  const handleApplyVolume = (index: number) => {
    const updated = [...pairs];
    persistPairsLocal(updated);
    void saveCopyTradeConfig(updated, isCopyTradeEngineRunning());

    if (isCopyTradeEngineRunning()) {
      forceApplyConfigs(updated.map(pairToConfig));
    }

    setAppliedIndex(index);
    if (applyTimerRef.current) clearTimeout(applyTimerRef.current);
    applyTimerRef.current = setTimeout(() => setAppliedIndex(null), 1500);
  };

  const formatTime = (iso: string): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  };

  // Vietnam time (GMT+7) clock, HH:MM:SS — the same timezone the engine/EA use
  // for the late-entry comparison.
  const fmtVnClock = (iso: string | null): string => {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return '—';
    return new Date(t + 7 * 3600 * 1000).toISOString().slice(11, 19);
  };

  // Vietnam date+time (GMT+7), e.g. "10-06 14:32" — used in the history list.
  const fmtVnDateTime = (iso: string | null): string => {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return '—';
    return new Date(t + 7 * 3600 * 1000).toISOString().slice(5, 16).replace('T', ' ');
  };

  const fmtAgeMinutes = (iso: string | null): string => {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return '—';
    return Math.max(0, Math.floor((Date.now() - t) / 60000)) + 'm';
  };

  const pendingApprovals = approvals.filter((a) => a.status === 'pending');
  const decidedApprovals = approvals.filter((a) => a.status !== 'pending');
  const historyApprovals = decidedApprovals.filter((a) =>
    historyFilter === 'all' ? true : a.status === historyFilter
  );
  const approvedCount = approvals.filter((a) => a.status === 'approved').length;
  const rejectedCount = approvals.filter((a) => a.status === 'rejected').length;
  const lateTargets = Array.from(new Set(pairs.map((p) => p.targetMt4AccountId)));

  // Decide a late-entry approval straight from the web (the EA reads the row on
  // its next poll, so it takes effect within ~0.5s — no Telegram needed).
  const handleApprovalDecision = async (row: ApprovalRow, decision: 'approved' | 'rejected') => {
    setApprovalBusyId(row.id);
    try {
      await supabase
        .from('copy_trade_approvals')
        .update({ status: decision, responded_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .eq('id', row.id)
        .eq('status', 'pending');
    } catch (err) {
      console.warn('[CopyTradeMonitor] approval decision failed:', err);
    } finally {
      setApprovalBusyId(null);
      void fetchApprovals();
    }
  };

  // Approve every pending late-entry request in one shot. Only rows that are
  // still 'pending' are touched (the .eq guard keeps it idempotent), so the EA
  // picks them all up on its next ~0.5s poll.
  const handleApproveAll = async () => {
    const ids = pendingApprovals.map((a) => a.id);
    if (ids.length === 0) return;
    setApproveAllBusy(true);
    try {
      await supabase
        .from('copy_trade_approvals')
        .update({ status: 'approved', responded_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .in('id', ids)
        .eq('status', 'pending');
    } catch (err) {
      console.warn('[CopyTradeMonitor] approve all failed:', err);
    } finally {
      setApproveAllBusy(false);
      void fetchApprovals();
    }
  };

  // Reject every pending late-entry request in one shot. Symmetric to
  // handleApproveAll — the EA sees the rejected rows on its next ~0.5s poll and
  // skips those positions (never asks again). Only 'pending' rows are touched.
  const handleRejectAll = async () => {
    const ids = pendingApprovals.map((a) => a.id);
    if (ids.length === 0) return;
    setRejectAllBusy(true);
    try {
      await supabase
        .from('copy_trade_approvals')
        .update({ status: 'rejected', responded_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .in('id', ids)
        .eq('status', 'pending');
    } catch (err) {
      console.warn('[CopyTradeMonitor] reject all failed:', err);
    } finally {
      setRejectAllBusy(false);
      void fetchApprovals();
    }
  };

  // Persist a per-target late-entry config. The bridge returns it in get_state,
  // so the EA applies it without any MT4-side edit.
  const saveLateConfig = async (target: string, patch: Partial<LateApprovalConfig>) => {
    const current = lateConfigs[target] || { target_mt4_account_id: target, enabled: true, grace_minutes: 5 };
    const next: LateApprovalConfig = { ...current, ...patch };
    setLateConfigs((prev) => ({ ...prev, [target]: next }));
    try {
      await supabase.from('copy_trade_late_config').upsert({
        target_mt4_account_id: target,
        enabled: next.enabled,
        grace_minutes: next.grace_minutes,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'target_mt4_account_id' });
    } catch (err) {
      console.warn('[CopyTradeMonitor] save late config failed:', err);
    }
  };

  // ─── Render ──────────────────────────────────────────────

  if (!isCtraderConnected) return null;

  return (
    <div className="overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-background-200/70 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <i className="ri-swap-line text-emerald-400 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground-900">Copy Trade Engine</h3>
            <p className="text-foreground-500 text-[11px]">
              cTrader → MT4 real-time mirror
              {engineRunning && (
                <span className="text-emerald-400 ml-1">
                  ● Live — {pairs.filter((p) => p.enabled !== false).length}/{pairs.length} pair{pairs.length > 1 ? 's' : ''}
                </span>
              )}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleToggleEngine}
          disabled={!isCtraderConnected || ctraderAccounts.length === 0 || mt4Accounts.length === 0}
          className={cn(
            'px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed',
            engineRunning
              ? 'bg-red-500 hover:bg-red-600 text-background-50'
              : 'bg-emerald-500 hover:bg-emerald-600 text-background-50'
          )}
        >
          <i className={engineRunning ? 'ri-stop-circle-line' : 'ri-play-circle-line'}></i>
          {engineRunning ? 'Stop Engine' : 'Start Engine'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-5 mt-4 bg-red-500/5 border border-red-500/10 rounded-lg p-3 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-400 text-xs mt-0.5 shrink-0"></i>
          <p className="text-red-400 text-xs flex-1">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-500 cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-xs"></i>
          </button>
        </div>
      )}

      <div className="p-5 space-y-4">
        {/* Copy Pairs */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide">
              Copy Pairs ({pairs.length})
            </h4>
            {!engineRunning && (
              <button
                type="button"
                onClick={() => setShowAddPair(!showAddPair)}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                Add Pair
              </button>
            )}
          </div>

          {/* Add Pair Form */}
          {showAddPair && (
            <div className="p-3 bg-background-50 rounded-lg border border-background-200/70 mb-3 space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] font-medium text-foreground-500 uppercase tracking-wide block mb-1">
                    cTrader Source
                  </label>
                  <select
                    value={newSourceId}
                    onChange={(e) => setNewSourceId(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-background-100 border border-background-300/60 rounded-md text-[10px] text-foreground-900 focus:outline-none focus:border-primary-500/50 cursor-pointer"
                  >
                    <option value="">Select source...</option>
                    {ctraderAccounts.map((acc) => (
                      <option key={acc.account_id} value={acc.account_id}>
                        {acc.account_name} ({acc.is_live ? 'Live' : 'Demo'})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[9px] font-medium text-foreground-500 uppercase tracking-wide block mb-1">
                    MT4 Target
                  </label>
                  <select
                    value={newTargetId}
                    onChange={(e) => setNewTargetId(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-background-100 border border-background-300/60 rounded-md text-[10px] text-foreground-900 focus:outline-none focus:border-primary-500/50 cursor-pointer"
                  >
                    <option value="">Select target...</option>
                    {mt4Accounts.map((acc) => (
                      <option key={acc.account_id} value={acc.account_id}>
                        {acc.account_name} ({acc.is_live ? 'Live' : 'Demo'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => { setShowAddPair(false); setError(null); }}
                  className="px-3 py-1.5 text-[10px] text-foreground-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddPair}
                  disabled={!newSourceId || !newTargetId}
                  className="px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
                >
                  Add Pair
                </button>
              </div>
            </div>
          )}

          {/* Pair Cards */}
          {pairs.length > 0 ? (
            <div className="space-y-2">
              {pairs.map((pair, idx) => {
                const pairEnabled = pair.enabled !== false;
                const volumeMode = pair.volumeMode || 'same';
                return (
                  <div
                    key={idx}
                    className={cn(
                      'p-3 rounded-lg border transition-colors',
                      pairEnabled
                        ? 'bg-background-50 border-background-200/70'
                        : 'bg-background-100/50 border-background-200/40'
                    )}
                  >
                    <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1 min-w-0">
                      <div className={cn('flex items-center gap-1.5 flex-wrap min-w-0', !pairEnabled && 'opacity-50')}>
                        {editingLabelIndex === idx ? (
                          <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            <input
                              type="text"
                              value={labelInput}
                              onChange={(e) => setLabelInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveLabel(idx);
                                if (e.key === 'Escape') handleCancelEditLabel();
                              }}
                              placeholder="Tên ghi chú..."
                              autoFocus
                              className="flex-1 min-w-0 px-2 py-1 bg-background-100 border border-primary-500/40 rounded-md text-[11px] text-foreground-900 focus:outline-none focus:border-primary-500"
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveLabel(idx)}
                              className="text-emerald-400 hover:text-emerald-500 cursor-pointer shrink-0"
                              title="Save"
                            >
                              <i className="ri-check-line text-xs"></i>
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEditLabel}
                              className="text-foreground-400 hover:text-foreground-600 cursor-pointer shrink-0"
                              title="Cancel"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className="text-xs font-mono font-medium text-foreground-900 break-all min-w-0">
                              {pair.label || pair.sourceAccountName}
                            </span>
                            <i className="ri-arrow-right-line text-foreground-400 text-xs shrink-0"></i>
                            <span className="text-xs font-mono font-medium text-foreground-900 break-all min-w-0">
                              {pair.label ? pair.targetMt4AccountName : pair.targetMt4AccountName}
                            </span>
                            {pair.label && (
                              <span className="text-[9px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded border border-background-200/60 shrink-0">
                                {pair.sourceAccountName} → {pair.targetMt4AccountName}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <span className={cn('text-[9px] mt-1 block', pairEnabled ? 'text-foreground-400' : 'text-foreground-300')}>
                        cTrader → MT4
                        {pair.label && (
                          <span className="ml-1.5 text-primary-400">· {pair.label}</span>
                        )}
                        {!pair.label && (
                          <button
                            type="button"
                            onClick={() => handleStartEditLabel(idx)}
                            className="ml-1.5 text-foreground-400 hover:text-primary-400 cursor-pointer transition-colors"
                            title="Add label"
                          >
                            <i className="ri-pencil-line text-[9px]"></i>
                          </button>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleTogglePair(idx)}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap border',
                          pairEnabled
                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                            : 'bg-background-100 border-background-300/60 text-foreground-400 hover:border-foreground-400/60'
                        )}
                      >
                        <span className={cn('w-1.5 h-1.5 rounded-full', pairEnabled ? 'bg-emerald-400' : 'bg-foreground-300')}></span>
                        {pairEnabled ? 'ON' : 'OFF'}
                      </button>
                      {confirmRemoveIndex === idx ? (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleRemovePair(idx)}
                            className="text-red-400 hover:text-red-500 cursor-pointer transition-colors shrink-0"
                            title="Confirm delete"
                          >
                            <i className="ri-check-line text-xs"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmRemoveIndex(null)}
                            className="text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors shrink-0"
                            title="Cancel"
                          >
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmRemoveIndex(idx)}
                          className="text-foreground-400 hover:text-red-400 cursor-pointer transition-colors shrink-0"
                          title="Remove pair"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      )}
                    </div>
                    </div>

                    {/* Volume transform config */}
                    <div className="mt-3 pt-3 border-t border-background-200/50">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
                        <label className="text-[9px] font-medium text-foreground-500 uppercase tracking-wide shrink-0">
                          Volume
                        </label>
                        <select
                          value={volumeMode}
                          onChange={(e) => updateVolumeMode(idx, e.target.value as 'same' | 'percent' | 'fixed')}
                          className="w-full sm:w-auto px-2.5 py-1.5 bg-background-100 border border-background-300/60 rounded-md text-[10px] text-foreground-900 focus:outline-none focus:border-primary-500/50 cursor-pointer"
                        >
                          <option value="same">1:1 (same as signal)</option>
                          <option value="percent">% of signal</option>
                          <option value="fixed">Fixed lot</option>
                        </select>

                        {volumeMode === 'percent' && (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              defaultValue={pair.volumePercent ?? ''}
                              onChange={(e) => updateVolumePercent(idx, e.target.value)}
                              placeholder="100"
                              className="w-20 px-2.5 py-1.5 bg-background-100 border border-background-300/60 rounded-md text-[10px] text-foreground-900 focus:outline-none focus:border-primary-500/50"
                            />
                            <span className="text-[10px] text-foreground-500">%</span>
                          </div>
                        )}

                        {volumeMode === 'fixed' && (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              defaultValue={pair.fixedVolume ?? ''}
                              onChange={(e) => updateFixedVolume(idx, e.target.value)}
                              placeholder="0.50"
                              className="w-20 px-2.5 py-1.5 bg-background-100 border border-background-300/60 rounded-md text-[10px] text-foreground-900 focus:outline-none focus:border-primary-500/50"
                            />
                            <span className="text-[10px] text-foreground-500">lot</span>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() => handleApplyVolume(idx)}
                          className={cn(
                            'ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap border',
                            appliedIndex === idx
                              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                              : 'bg-primary-500 hover:bg-primary-600 text-background-50 border-transparent'
                          )}
                        >
                          {appliedIndex === idx ? (
                            <>
                              <i className="ri-check-line"></i>
                              Applied
                            </>
                          ) : (
                            <>
                              <i className="ri-check-double-line"></i>
                              Apply
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed border-background-300/60 rounded-lg">
              <p className="text-foreground-500 text-xs">
                {ctraderAccounts.length === 0 || mt4Accounts.length === 0
                  ? 'Need both cTrader and MT4 accounts connected.'
                  : 'Add a copy pair to start mirroring trades.'}
              </p>
            </div>
          )}
        </div>

        {/* Late-entry approvals — review & decide on the web (pending + history) */}
        <div>
          <div className="flex items-center justify-between mb-2 gap-2">
            <h4 className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              Duyệt lệnh muộn
            </h4>
            <button
              type="button"
              onClick={() => void fetchApprovals()}
              className="text-[10px] text-primary-400 hover:text-primary-500 cursor-pointer"
              title="Refresh"
            >
              <i className="ri-refresh-line"></i>
            </button>
          </div>

          {/* View switch: pending vs history */}
          <div className="inline-flex items-center gap-1 p-1 bg-background-100 border border-background-200/70 rounded-full mb-3">
            <button
              type="button"
              onClick={() => setApprovalView('pending')}
              className={cn(
                'px-3 py-1 rounded-full text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap',
                approvalView === 'pending'
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-500 hover:text-foreground-700'
              )}
            >
              Chờ duyệt ({pendingApprovals.length})
            </button>
            <button
              type="button"
              onClick={() => setApprovalView('history')}
              className={cn(
                'px-3 py-1 rounded-full text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap',
                approvalView === 'history'
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-500 hover:text-foreground-700'
              )}
            >
              Lịch sử ({decidedApprovals.length})
            </button>
          </div>

          {approvalView === 'pending' ? (
            pendingApprovals.length === 0 ? (
              <div className="text-center py-3 border border-dashed border-background-300/60 rounded-lg">
                <p className="text-foreground-500 text-xs">Không có lệnh nào đang chờ duyệt.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-end gap-2 flex-wrap">
                  <button
                    type="button"
                    disabled={rejectAllBusy}
                    onClick={() => void handleRejectAll()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-semibold bg-background-100 border border-background-300/60 text-foreground-600 hover:border-red-400/60 hover:text-red-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    <i className={rejectAllBusy ? 'ri-loader-4-line animate-spin' : 'ri-close-circle-line'}></i>
                    Từ chối tất cả ({pendingApprovals.length})
                  </button>
                  <button
                    type="button"
                    disabled={approveAllBusy}
                    onClick={() => void handleApproveAll()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-semibold bg-emerald-500 hover:bg-emerald-600 text-background-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    <i className={approveAllBusy ? 'ri-loader-4-line animate-spin' : 'ri-check-double-line'}></i>
                    Duyệt tất cả ({pendingApprovals.length})
                  </button>
                </div>
                {pendingApprovals.map((row) => (
                  <div key={row.id} className="p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/20">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={cn('text-xs font-mono font-semibold', row.trade_side === 'SELL' ? 'text-red-400' : 'text-emerald-400')}>
                            {row.symbol_name || '—'}
                          </span>
                          <span className={cn('text-[10px] font-medium', row.trade_side === 'SELL' ? 'text-red-400' : 'text-emerald-400')}>
                            {row.trade_side || ''}
                          </span>
                          <span className="text-[10px] text-foreground-600 font-mono">
                            {row.volume != null ? Number(row.volume).toFixed(2) : '—'} lots
                          </span>
                        </div>
                        <div className="text-[9px] text-foreground-400 mt-0.5 flex flex-wrap items-center gap-x-2">
                          <span>MT4: <span className="font-mono text-foreground-500">{row.target_mt4_account_id}</span></span>
                          <span>Mở lúc: {fmtVnClock(row.source_open_time)} (GMT+7)</span>
                          <span className="text-amber-500">· đã {fmtAgeMinutes(row.source_open_time)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          disabled={approvalBusyId === row.id}
                          onClick={() => handleApprovalDecision(row, 'approved')}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold bg-emerald-500 hover:bg-emerald-600 text-background-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          <i className="ri-check-line"></i>
                          Cho vào
                        </button>
                        <button
                          type="button"
                          disabled={approvalBusyId === row.id}
                          onClick={() => handleApprovalDecision(row, 'rejected')}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold bg-background-100 border border-background-300/60 text-foreground-600 hover:border-red-400/60 hover:text-red-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          <i className="ri-close-line"></i>
                          Không
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div>
              {/* History filter chips */}
              <div className="flex items-center gap-1.5 flex-wrap mb-2">
                {([
                  { key: 'all' as const, label: `Tất cả (${decidedApprovals.length})` },
                  { key: 'approved' as const, label: `Đã duyệt (${approvedCount})` },
                  { key: 'rejected' as const, label: `Từ chối (${rejectedCount})` },
                ]).map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setHistoryFilter(f.key)}
                    className={cn(
                      'px-2.5 py-1 rounded-full text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap border',
                      historyFilter === f.key
                        ? 'bg-secondary-100 border-secondary-200 text-secondary-900'
                        : 'bg-background-100 border-background-300/60 text-foreground-500 hover:text-foreground-700'
                    )}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {historyApprovals.length === 0 ? (
                <div className="text-center py-3 border border-dashed border-background-300/60 rounded-lg">
                  <p className="text-foreground-500 text-xs">Chưa có lệnh nào được duyệt hoặc từ chối.</p>
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-background-200/70 divide-y divide-background-200/20">
                  {historyApprovals.map((row) => {
                    const approved = row.status === 'approved';
                    return (
                      <div key={row.id} className="px-2.5 py-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={cn(
                              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold',
                              approved ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                            )}>
                              <i className={approved ? 'ri-check-line' : 'ri-close-line'}></i>
                              {approved ? 'Đã duyệt' : 'Từ chối'}
                            </span>
                            <span className={cn('text-xs font-mono font-semibold', row.trade_side === 'SELL' ? 'text-red-400' : 'text-emerald-400')}>
                              {row.symbol_name || '—'}
                            </span>
                            <span className="text-[10px] text-foreground-600">{row.trade_side || ''}</span>
                            <span className="text-[10px] text-foreground-600 font-mono">
                              {row.volume != null ? Number(row.volume).toFixed(2) : '—'} lots
                            </span>
                          </div>
                          <div className="text-[9px] text-foreground-400 mt-0.5 flex flex-wrap items-center gap-x-2">
                            <span>MT4: <span className="font-mono text-foreground-500">{row.target_mt4_account_id}</span></span>
                            <span>Mở: {fmtVnDateTime(row.source_open_time)}</span>
                            <span>Xử lý: {fmtVnDateTime(row.responded_at)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Late-entry approval config — synced to the EA via get_state */}
        {lateTargets.length > 0 && (
          <div>
            <h4 className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <i className="ri-shield-check-line"></i>
              Duyệt lệnh muộn (đồng bộ MT4)
            </h4>
            <div className="space-y-2">
              {lateTargets.map((target) => {
                const cfg = lateConfigs[target] || { target_mt4_account_id: target, enabled: true, grace_minutes: 5 };
                return (
                  <div key={target} className="p-2.5 rounded-lg bg-background-50 border border-background-200/70 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <span className="text-[11px] font-mono text-foreground-800">MT4 {target}</span>
                      <p className="text-[9px] text-foreground-400 mt-0.5">
                        Lệnh cTrader cũ hơn ngưỡng sẽ chờ duyệt thay vì tự vào.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => void saveLateConfig(target, { enabled: !cfg.enabled })}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap border',
                          cfg.enabled
                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                            : 'bg-background-100 border-background-300/60 text-foreground-400 hover:border-foreground-400/60'
                        )}
                      >
                        <span className={cn('w-1.5 h-1.5 rounded-full', cfg.enabled ? 'bg-emerald-400' : 'bg-foreground-300')}></span>
                        {cfg.enabled ? 'BẬT' : 'TẮT'}
                      </button>
                      <div className="flex items-center gap-1.5">
                        <LateGraceInput
                          value={cfg.grace_minutes}
                          onCommit={(n) => void saveLateConfig(target, { grace_minutes: n })}
                        />
                        <span className="text-[10px] text-foreground-500">phút</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Desired State / Open Positions */}
        {engineRunning && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide">
                Open Positions on cTrader (being mirrored)
              </h4>
              <button
                type="button"
                onClick={() => void fetchState()}
                className="text-[10px] text-primary-400 hover:text-primary-500 cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
              </button>
            </div>

            {/* Pair status summary */}
            <div className="space-y-1.5 mb-3">
              {stateEntries.length === 0 && (
                <div className="text-center py-3 border border-dashed border-background-300/60 rounded-lg">
                  <p className="text-foreground-500 text-xs">No sync state yet — waiting for first reconcile…</p>
                </div>
              )}
              {stateEntries.map((entry, i) => (
                <div key={i} className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', entry.isActive ? 'bg-emerald-400' : 'bg-foreground-300')}></span>
                    <span className="text-[10px] font-mono text-foreground-800 truncate">
                      {entry.sourceAccountId} → {entry.targetMt4AccountId}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={cn('text-[9px] font-medium', entry.isActive ? 'text-emerald-400' : 'text-foreground-400')}>
                      {entry.isActive ? '● Active' : 'Paused'} · {entry.positions.length} pos
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDeleteStateEntry(entry)}
                      className="text-foreground-400 hover:text-red-400 cursor-pointer transition-colors"
                      title="Remove this sync pair"
                    >
                      <i className="ri-delete-bin-line text-[10px]"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Flat position list */}
            {(() => {
              const allPositions = stateEntries
                .filter((e) => e.isActive)
                .flatMap((e) => e.positions.map((p) => ({ ...p, target: e.targetMt4AccountId })));
              if (allPositions.length === 0) {
                return (
                  <div className="text-center py-4 border border-dashed border-background-300/60 rounded-lg">
                    <p className="text-foreground-500 text-xs">No open positions to mirror right now.</p>
                  </div>
                );
              }
              return (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-background-200/70">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="border-b border-background-200/70 sticky top-0 bg-background-50">
                        <th className="text-left px-2 py-1.5 text-foreground-500 font-medium">Symbol</th>
                        <th className="text-left px-2 py-1.5 text-foreground-500 font-medium">Side</th>
                        <th className="text-right px-2 py-1.5 text-foreground-500 font-medium">Lots</th>
                        <th className="text-right px-2 py-1.5 text-foreground-500 font-medium">SL</th>
                        <th className="text-right px-2 py-1.5 text-foreground-500 font-medium">TP</th>
                        <th className="text-left px-2 py-1.5 text-foreground-500 font-medium">MT4</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allPositions.map((p, i) => (
                        <tr key={i} className="border-b border-background-200/20 hover:bg-background-50 transition-colors">
                          <td className="px-2 py-1 text-foreground-800 font-mono font-medium">{p.symbolName}</td>
                          <td className="px-2 py-1">
                            <span className={cn('text-[9px] font-medium', p.tradeSide === 'BUY' ? 'text-emerald-400' : 'text-red-400')}>
                              {p.tradeSide}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-right text-foreground-700 font-mono">{Number(p.volume).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right text-foreground-600 font-mono">{p.sl != null ? p.sl : '—'}</td>
                          <td className="px-2 py-1 text-right text-foreground-600 font-mono">{p.tp != null ? p.tp : '—'}</td>
                          <td className="px-2 py-1 text-foreground-500 font-mono whitespace-nowrap">{p.target}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}